import React, { useState, Component } from 'react';
import { Button } from '../ui/Button';
import { Toggle } from '../ui/Toggle';
import { Badge } from '../ui/Badge';
import { X, Star, Tag, Layers, Ruler, DollarSign, ImageOff } from 'lucide-react';
import type { ResolvedOption, Scope, OptionOverride, GlobalOption } from '../../data/optionsData';
type OptionType = 'Standard' | 'Upgrade' | 'Custom';
type LevelTier = 0 | 1 | 2 | 3;
type Uom = 'EA' | 'LF' | 'SF' | 'SY' | 'CY';
const TYPE_OPTIONS: OptionType[] = ['Standard', 'Upgrade', 'Custom'];
const LEVEL_OPTIONS: LevelTier[] = [0, 1, 2, 3];
const UOM_OPTIONS: {
  value: Uom;
  label: string;
}[] = [{
  value: 'EA',
  label: 'EA – Each'
}, {
  value: 'LF',
  label: 'LF – Linear Feet'
}, {
  value: 'SF',
  label: 'SF – Square Feet'
}, {
  value: 'SY',
  label: 'SY – Square Yards'
}, {
  value: 'CY',
  label: 'CY – Cubic Yards'
}];
const LEVEL_STYLES: Record<LevelTier, {
  className: string;
}> = {
  0: {
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  },
  1: {
    className: 'bg-blue-50 text-blue-700 border-blue-200'
  },
  2: {
    className: 'bg-violet-50 text-violet-700 border-violet-200'
  },
  3: {
    className: 'bg-amber-50 text-amber-700 border-amber-200'
  }
};
export function EditOptionModal({
  isOpen,
  onClose,
  option,
  scope,
  onSave,
  onSaveGlobal
}: {
  isOpen: boolean;
  onClose: () => void;
  option: ResolvedOption;
  scope: Scope;
  onSave: (override: OptionOverride) => void;
  onSaveGlobal: (optionId: string, updates: Partial<GlobalOption>) => void;
}) {
  // Core persisted fields
  const [title, setTitle] = useState<string>(option.effectiveName);
  const [description, setDescription] = useState<string>(option.effectiveDescription || '');
  const [basePrice, setBasePrice] = useState<string>(option.effectivePrice.toString());
  const [enabled, setEnabled] = useState<boolean>(option.effectiveEnabled);
  const [isStandard, setIsStandard] = useState<boolean>(option.effectiveIsStandard);
  // New UI-only fields (visual layout, persistence wiring TBD)
  const [type, setType] = useState<OptionType>(option.effectiveIsStandard ? 'Standard' : 'Upgrade');
  const [level, setLevel] = useState<LevelTier>(0);
  const [uom, setUom] = useState<Uom>('EA');
  const [isDefault, setIsDefault] = useState(false);
  const [salesOffice, setSalesOffice] = useState(false);
  const [designCenter, setDesignCenter] = useState(true);
  const [productId, setProductId] = useState<string>(option.id);
  const isGlobal = scope.level === 'global';
  const levelStyle = LEVEL_STYLES[level];
  if (!isOpen) return null;
  const handleSave = () => {
    // Keep type<->isStandard in sync on save
    const effectiveIsStandard = type === 'Standard';
    if (isGlobal) {
      const updates: Partial<GlobalOption> = {
        name: title,
        description,
        basePrice: parseFloat(basePrice) || 0,
        enabled,
        isStandard: effectiveIsStandard
      };
      onSaveGlobal(option.id, updates);
    } else {
      const override: OptionOverride = {
        optionId: option.id,
        level: scope.level as 'region' | 'community',
        levelId: scope.level === 'region' ? scope.regionId! : scope.communityId!,
        nameOverride: title,
        descriptionOverride: description,
        priceOverride: parseFloat(basePrice) || 0,
        enabledOverride: enabled,
        isStandardOverride: effectiveIsStandard
      };
      onSave(override);
    }
    onClose();
  };
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-401">
      <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[92vh] flex flex-col overflow-hidden" data-id="element-402">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100 flex-shrink-0" data-id="element-403">
          <div data-id="element-404">
            <h2 className="text-lg font-semibold text-slate-900" data-id="element-405">
              {isGlobal ? 'Edit Option' : 'Override Option'}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5" data-id="element-406">
              {isGlobal ? 'Editing global defaults — affects all communities' : `Customizing for ${scope.name}`}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 -mt-1 -mr-1 p-1.5 rounded-lg hover:bg-slate-100 transition-colors" aria-label="Close" data-id="element-407">
            <X className="h-4 w-4" data-id="element-408" />
          </button>
        </div>

        {/* Identity Preview */}
        <div className="px-6 pt-5 flex-shrink-0" data-id="element-409">
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100" data-id="element-410">
            {option.hasImage && option.imageUrl ? <img src={option.imageUrl} alt={option.name} className="w-14 h-14 object-cover rounded-md bg-white border border-slate-200 flex-shrink-0" data-id="element-411" /> : <div className="w-14 h-14 bg-white border border-slate-200 rounded-md flex items-center justify-center flex-shrink-0" data-id="element-412">
                <ImageOff className="h-5 w-5 text-slate-300" data-id="element-413" />
              </div>}
            <div className="flex-1 min-w-0" data-id="element-414">
              <div className="flex items-center gap-2" data-id="element-415">
                <p className="font-semibold text-slate-900 truncate" data-id="element-416">
                  {title || 'Untitled Option'}
                </p>
                {type === 'Standard' ? <Badge variant="success" className="text-[11px] flex-shrink-0" data-id="element-417">
                    <Star className="h-3 w-3 mr-1" data-id="element-418" />
                    Included
                  </Badge> : <Badge variant="neutral" className="text-[11px] flex-shrink-0 bg-slate-100 text-slate-600 border-slate-200" data-id="element-419">
                    {type}
                  </Badge>}
              </div>
              <p className="text-xs text-slate-500 mt-0.5 truncate" data-id="element-420">
                {option.category}
                {option.subcategory ? ` · ${option.subcategory}` : ''} ·{' '}
                {productId}
              </p>
            </div>
          </div>
        </div>

        {/* Body (scroll) */}
        <div className="px-6 py-5 space-y-6 overflow-y-auto flex-1" data-id="element-421">
          {/* ── BASICS ───────────────────────────────────── */}
          <Section title="Basics" data-id="element-422">
            <Field label="Title" data-id="element-423">
              <input type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white" data-id="element-424" />
            </Field>
            <Field label="Description" hint="Shown to buyers in the catalog" data-id="element-425">
              <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="Add a short description…" className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white resize-none placeholder:text-slate-400" data-id="element-426" />
            </Field>
          </Section>

          {/* ── CLASSIFICATION ────────────────────────────── */}
          <Section title="Classification" data-id="element-427">
            <div className="grid grid-cols-3 gap-3" data-id="element-428">
              <Field label="Type" icon={Tag} data-id="element-429">
                <select value={type} onChange={e => setType(e.target.value as OptionType)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white" data-id="element-430">
                  {TYPE_OPTIONS.map(t => <option key={t} value={t} data-id="element-431">
                      {t}
                    </option>)}
                </select>
              </Field>
              <Field label="Level" icon={Layers} data-id="element-432">
                <select value={level} onChange={e => setLevel(parseInt(e.target.value, 10) as LevelTier)} className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 font-medium ${levelStyle.className}`} data-id="element-433">
                  {LEVEL_OPTIONS.map(l => <option key={l} value={l} data-id="element-434">
                      Level {l}
                    </option>)}
                </select>
              </Field>
              <Field label="Unit" icon={Ruler} data-id="element-435">
                <select value={uom} onChange={e => setUom(e.target.value as Uom)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white" data-id="element-436">
                  {UOM_OPTIONS.map(u => <option key={u.value} value={u.value} data-id="element-437">
                      {u.value}
                    </option>)}
                </select>
              </Field>
            </div>
          </Section>

          {/* ── PRICING ───────────────────────────────────── */}
          <Section title="Pricing" data-id="element-438">
            <Field label="Base Price" hint={!isGlobal ? `Was $${(option.parentValue?.price ?? option.basePrice).toLocaleString()}` : undefined} data-id="element-439">
              <div className="relative" data-id="element-440">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-441" />
                <input type="number" value={basePrice} onChange={e => setBasePrice(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white" data-id="element-442" />
              </div>
              {type === 'Standard' && parseFloat(basePrice) > 0 && <p className="text-[11px] text-amber-600 mt-1" data-id="element-443">
                  Standard options typically have $0 since they're included.
                </p>}
            </Field>
          </Section>

          {/* ── VISIBILITY ────────────────────────────────── */}
          <Section title="Visibility & Behavior" data-id="element-444">
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100" data-id="element-445">
              <ToggleRow label="Enabled" description="Available for buyers to select" checked={enabled} onChange={setEnabled} data-id="element-446" />
              <ToggleRow label="Default Option" description="Pre-selected for new buyers" checked={isDefault} onChange={setIsDefault} data-id="element-447" />
              <ToggleRow label="Sales Office" description="Visible in the sales office view" checked={salesOffice} onChange={setSalesOffice} data-id="element-448" />
              <ToggleRow label="Design Center" description="Visible in the design center view" checked={designCenter} onChange={setDesignCenter} data-id="element-449" />
            </div>
          </Section>

          {/* ── REFERENCE ─────────────────────────────────── */}
          <Section title="Reference" data-id="element-450">
            <Field label="Product ID" hint="Internal catalog identifier" data-id="element-451">
              <input type="text" value={productId} onChange={e => setProductId(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white font-mono text-xs" data-id="element-452" />
            </Field>
          </Section>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50 flex-shrink-0" data-id="element-453">
          <Button variant="ghost" size="sm" onClick={onClose} data-id="element-454">
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} data-id="element-455">
            {isGlobal ? 'Save Changes' : 'Save Override'}
          </Button>
        </div>
      </div>
    </div>;
}
// ─── Subcomponents ────────────────────────────────────────────────────────
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div className="space-y-3" data-id="element-456">
      <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider" data-id="element-457">
        {title}
      </h3>
      <div className="space-y-3" data-id="element-458">{children}</div>
    </div>;
}
function Field({
  label,
  hint,
  icon: Icon,
  children
}: {
  label: string;
  hint?: string;
  icon?: ComponentType<{
    className?: string;
  }>;
  children: React.ReactNode;
}) {
  return <div className="space-y-1.5" data-id="element-459">
      <div className="flex items-center justify-between gap-2" data-id="element-460">
        <label className="text-xs font-medium text-slate-700 flex items-center gap-1.5" data-id="element-461">
          {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" data-id="element-462" />}
          {label}
        </label>
        {hint && <span className="text-[11px] text-slate-400" data-id="element-463">{hint}</span>}
      </div>
      {children}
    </div>;
}
function ToggleRow({
  label,
  description,
  checked,
  onChange
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return <div className="flex items-center justify-between gap-4 px-4 py-3" data-id="element-464">
      <div className="min-w-0" data-id="element-465">
        <p className="text-sm font-medium text-slate-900" data-id="element-466">{label}</p>
        <p className="text-xs text-slate-500 mt-0.5" data-id="element-467">{description}</p>
      </div>
      <Toggle checked={checked} onChange={onChange} size="sm" data-id="element-468" />
    </div>;
}